/**
 * Define all of your application routes here
 * for more information on routes, see the
 * official documentation https://router.vuejs.org/en/
 */

import { Routes } from '@/helpers/consts';

const getPathName = path => (path.name || path.view).replace(/^\w+?\//, '');

const mapChildrenNames = (children, title) =>
  children.map(({ parent, isRoot, hidden, ...c }) => ({
    ...c,
    path: c.path.replace(/^\//, ''),
    name: getPathName(c),
    query: c.query,
    meta: { ...(c.meta || {}), title, parent, hidden, isRoot },
  }));

const getTabbedView = (children, name) => {
  let path = children[0].path || '';

  children[0].path = '';
  path = path[0] === '/' ? path : '/' + path;

  const title = name || children[0].name || children[0].view;

  return {
    view: 'TabView',
    meta: { name, title },
    path,
    props: { children: mapChildrenNames(children.filter(p => !p.hidden)) },
    children: mapChildrenNames(children, title).sort((a, _b) => (a.path ? 1 : -1)),
  };
};

const paths = [
  {
    path: Routes.overview,
    view: 'Overview',
    meta: { permission: null },
  },

  getTabbedView(
    [
      {
        path: Routes.reviewId,
        view: 'Reviews',
      },
      {
        path: Routes.products,
        view: 'Products',
      },
      {
        path: Routes.smartReviews,
        view: 'SmartReviews',
      },

      {
        path: Routes.questionId,
        view: 'Questions',
        meta: { permission: 'q_and_a' },
      },
      {
        path: Routes.linkProducts,
        view: 'LinkProducts',
        meta: { permission: 'link_products' },
      },
    ],
    'ReviewsAndQA'
  ),

  getTabbedView([
    {
      path: Routes.importReviewsId,
      view: 'ImportReviews',
      meta: { permission: null },
    },
    {
      path: Routes.shopReviews,
      name: 'ShopReviews',
      view: 'ShopReviews',
      meta: { permission: null },
    },
    {
      path: Routes.migrateReviews,
      name: 'MigrateReviews',
      view: 'MigrateReviews',
      meta: { permission: null },
    },
  ]),
  getTabbedView(
    [
      {
        path: Routes.requestSchedule,
        view: 'RequestSchedule',
        props: true,
      },
      {
        path: Routes.reviewRequestsConfiguration,
        view: 'ReviewRequestsConfiguration',
        props: true,
        parent: 'aftership',
      },
      {
        path: Routes.unverifiedReviewSimpleEmail,
        view: 'Email/UnverifiedReviewSimpleEmail',
        name: 'UnverifiedReviewSimpleEmail',
      },
      {
        path: Routes.requestViaLink,
        view: 'RequestViaLink',
      },

      {
        path: Routes.requestEmailTemplate,
        view: 'RequestEmailTemplate',
        hidden: true,
        parent: 'ReviewRequestsConfiguration',
      },

      //      "advancedCouponTemplate": "Advanced Coupon Template",
      // "advancedEmailTemplate": "Advanced Email Template",
      // "advancedReminderEmailTemplate": "Advanced Reminder Email Template",
      // "advancedTemplates": "Advanced Templates",
      //
      {
        path: Routes.countryReviewRequestEmail,
        view: 'Email/ReviewRequestEmail',
        name: 'ReviewRequestEmail',
        hidden: true,
        parent: 'ReviewRequestsConfiguration',
        meta: { name: 'AdvancedReviewRequestEmail' },
      },

      {
        path: Routes.countryCouponEmail,
        view: 'Email/CouponEmail',
        name: 'CouponEmail',
        hidden: true,
        parent: 'ReviewRequestsConfiguration',
        meta: { name: 'AdvancedCouponEmail' },
      },
      {
        path: Routes.countryUnverifiedReviewConfirmationEmail,
        view: 'Email/UnverifiedReviewConfirmationEmail',
        name: 'UnverifiedReviewConfirmationEmail',
        hidden: true,
        meta: { name: 'UnverifiedReviewConfirmationEmail' },
        parent: 'UnverifiedReviewSimpleEmail',
      },
      {
        path: Routes.countryPhotoRequestEmail,
        view: 'Email/PhotoRequestEmail',
        name: 'PhotoRequestEmail',
        hidden: true,
        parent: 'ReviewRequestsConfiguration',
        meta: { name: 'AdvancedPhotoRequestEmail' },
      },

      {
        path: Routes.countryThankYouPageTemplate,
        view: 'Email/ThankYouPageTemplate',
        name: 'ThankYouPageTemplate',
        hidden: true,
        parent: 'ReviewRequestsConfiguration',
      },

      {
        path: Routes.orderPrinter,
        view: 'Email/OrderPrinterTemplate',
        name: 'OrderPrinterTemplate',
        hidden: true,
        parent: 'ReviewRequestsConfiguration',
      },

      {
        path: Routes.printedReviewRequests,
        view: 'PrintedReviewRequests',
        hidden: true,
        parent: 'ReviewRequestsConfiguration',
      },

      {
        path: Routes.couponEmailTemplate,
        view: 'CouponEmailTemplate',
        hidden: true,
        parent: 'ReviewRequestsConfiguration',
      },
      {
        path: Routes.photoEmailTemplate,
        view: 'PhotoEmailTemplate',
        hidden: true,
        parent: 'ReviewRequestsConfiguration',
      },
      {
        path: Routes.thankYouEmailTemplate,
        view: 'ThankYouTemplate',
        hidden: true,
        parent: 'ReviewRequestsConfiguration',
      },

      {
        path: Routes.templatesSMS,
        view: 'SMSTemplates',
        hidden: true,
        parent: 'ReviewRequestsConfiguration',
      },
    ],
    'EmailAndSmsRequests'
  ),

  getTabbedView(
    [
      {
        path: Routes.widgets,
        view: 'Widgets',
      },
      {
        path: Routes.customizeProduct,
        view: 'CustomizeProduct',
        hidden: true,
        parent: 'Widgets',
      },
      {
        path: Routes.customizePlugin,
        view: 'CustomizePlugin',
        hidden: true,
        parent: 'Widgets',
      },

      {
        path: Routes.customizeBadge,
        view: 'CustomizeBadge',
        hidden: true,
        parent: 'Widgets',
      },
      {
        path: Routes.allReviewsPage,
        view: 'AllReviews',
        hidden: true,
        parent: 'Widgets',
      },
      {
        path: Routes.customizeRequests,
        view: 'CustomizeRequests',
        hidden: true,
        parent: 'Widgets',
      },
      {
        path: Routes.customizeCarousel,
        view: 'CustomizeCarousel',
        hidden: true,
        parent: 'Widgets',
      },
      {
        path: Routes.customizeStars,
        view: 'CustomizeStars',
        hidden: true,
        parent: 'Widgets',
      },
      {
        path: Routes.customizeFloatingWidget,
        view: 'CustomizeFloatingWidget',
        hidden: true,
        parent: 'Widgets',
      },
      {
        path: Routes.advancedWidgets,
        view: 'AdvancedWidgets',
      },
      {
        path: Routes.settingsCustomCss,
        view: 'SettingsCustomCss',
      },
      {
        path: Routes.seo,
        view: 'Seo',
      },
    ],
    'Widgets'
  ),

  getTabbedView(
    [
      {
        path: Routes.integration,
        view: 'Integration',
        name: 'Integrations',
        isRoot: true,
        children: [
          { view: 'Integrations', path: '', isRoot: true },
          {
            path: Routes.googleShoppingIntegration,
            view: 'GoogleShoppingIntegration',
          },
          {
            path: Routes.aftershipIntegration,
            view: 'AftershipIntegration',
          },
          {
            path: Routes.orderPrinterIntegration,
            view: 'OrderPrinterIntegration',
          },
          {
            path: Routes.orderPrinterProIntegration,
            view: 'OrderPrinterProIntegration',
          },
          {
            path: Routes.klaviyoIntegration,
            view: 'KlaviyoIntegration',
          },
          {
            path: Routes.shopifyFlow,
            view: 'ShopifyFlow',
          },
          {
            path: Routes.pageflyIntegration,
            view: 'PageflyIntegration',
          },
          {
            path: Routes.gorgiasIntegration,
            view: 'GorgiasIntegration',
          },
          {
            path: Routes.zendeskIntegration,
            view: 'ZendeskIntegration',
          },
          {
            path: Routes.attentiveIntegration,
            view: 'AttentiveIntegration',
          },
          {
            path: Routes.omnisendIntegration,
            view: 'OmnisendIntegration',
          },
          {
            path: Routes.riseAiIntegration,
            view: 'RiseAiIntegration',
          },
          {
            path: Routes.smileIoIntegration,
            view: 'SmileIoIntegration',
          },
          {
            path: Routes.loyaltyLionIntegration,
            view: 'LoyaltyLionIntegration',
          },
          {
            path: Routes.salesforceIntegration,
            view: 'SalesforceIntegration',
          },
          {
            path: Routes.layouthubIntegration,
            view: 'LayouthubIntegration',
          },
          {
            path: Routes.weglotIntegration,
            view: 'WeglotIntegration',
          },
          {
            path: Routes.fastSimonIntegration,
            view: 'FastSimonIntegration',
          },
          {
            path: Routes.wiserIntegration,
            view: 'WiserIntegration',
          },
          {
            path: Routes.limespotIntegration,
            view: 'LimespotIntegration',
          },
          {
            path: Routes.gempagesIntegration,
            view: 'GempagesIntegration',
          },
          {
            path: Routes.carsonIntegration,
            view: 'CarsonIntegration',
          },
          {
            path: Routes.jsonLdForSeoIntegration,
            view: 'JsonLdForSeoIntegration',
          },
          {
            path: Routes.seoBoosterIntegration,
            view: 'SeoBoosterIntegration',
          },
          {
            path: Routes.rebuyIntegration,
            view: 'RebuyIntegration',
          },
          {
            path: Routes.seoAllInOneIntegration,
            view: 'SeoAllInOneIntegration',
          },
          {
            path: Routes.OberloIntegration,
            view: 'OberloIntegration',
          },
          {
            path: Routes.DropifiedIntegration,
            view: 'DropifiedIntegration',
          },
          {
            path: Routes.ImportifyIntegration,
            view: 'ImportifyIntegration',
          },
          {
            path: Routes.SpreadrIntegration,
            view: 'SpreadrIntegration',
          },
          {
            path: Routes.YoastIntegration,
            view: 'YoastIntegration',
          },
          {
            path: Routes.SeoImageIntegration,
            view: 'SeoImageIntegration',
          },
          // {
          //   path: Routes.bazaarvoiceIntegration,
          //   view: 'BazaarvoiceIntegration',
          // },
          {
            path: Routes.ZipifyIntegration,
            view: 'ZipifyIntegration',
          },
          {
            path: Routes.SchemaIntegration,
            view: 'SchemaIntegration',
          },
          {
            path: Routes.Agencies,
            view: 'Agencies',
          },
          {
            path: '/test-agency',
            view: 'TestAgency',
          },
          {
            path: Routes.HeyCarson,
            view: 'HeyCarson',
          },
          {
            path: Routes.PollardMedia,
            view: 'PollardMedia',
          },
          {
            path: Routes.BrandActive,
            view: 'BrandActive',
          },
          {
            path: Routes.BoldStrategies,
            view: 'BoldStrategies',
          },
          {
            path: Routes.FourSocial,
            view: 'FourSocial',
          },
          {
            path: Routes.HawkeMedia,
            view: 'HawkeMedia',
          },
          {
            path: Routes.AttentionGrabbing,
            view: 'AttentionGrabbing',
          },
          {
            path: Routes.Droomtech,
            view: 'Droomtech',
          },
          {
            path: Routes.RaysTechnology,
            view: 'RaysTechnology',
          },
          {
            path: Routes.ArrowIntegration,
            view: 'ArrowIntegration',
          },
          {
            path: Routes.BlueOwl,
            view: 'BlueOwl',
          },
          { path: '/', redirect: 'Integrations' },
        ],
      },
      {
        path: Routes.integrationFilter + '/2',
        view: 'IntegrationsFilter',
        name: 'IntegrationsCustomerSupport',
        props: { chosenCategory: 2 },
      },
      {
        path: Routes.integrationFilter + '/3',
        view: 'IntegrationsFilter',
        name: 'IntegrationsMarketing',
        props: { chosenCategory: 3 },
      },
      {
        path: Routes.integrationFilter + '/4',
        view: 'IntegrationsFilter',
        name: 'IntegrationsRecommendation',
        props: { chosenCategory: 4 },
      },
      {
        path: Routes.integrationFilter + '/6',
        view: 'IntegrationsFilter',
        name: 'IntegrationsPageBuilders',
        props: { chosenCategory: 6 },
      },
      {
        path: Routes.integrationFilter + '/8',
        view: 'IntegrationsFilter',
        name: 'IntegrationsProductImporters',
        props: { chosenCategory: 8 },
      },
      {
        path: Routes.integrationFilter + '/5',
        view: 'IntegrationsFilter',
        name: 'IntegrationsSeo',
        props: { chosenCategory: 5 },
      },
      {
        path: Routes.integrationFilter + '/9',
        view: 'IntegrationsFilter',
        name: 'IntegrationsShipping',
        props: { chosenCategory: 9 },
      },
      {
        path: Routes.integrationFilter + '/7',
        view: 'IntegrationsFilter',
        name: 'IntegrationsOthers',
        props: { chosenCategory: 7 },
      },

      {
        path: Routes.Digitlabs,
        view: 'Digitlabs',
        hidden: true,
      },

      {
        path: Routes.DsersIntegration,
        view: 'DsersIntegration',
        hidden: true,
      },
      {
        path: Routes.SeoImageCompression,
        view: 'SeoImageCompression',
        hidden: true,
      },
      {
        path: Routes.AutoQA,
        view: 'AutoQA',
        hidden: true,
      },
    ],
    'Integration'
  ),

  getTabbedView([
    {
      path: Routes.generalSettings,
      view: 'GeneralSettings',
    },
    {
      path: Routes.account,
      view: 'Account',
      props: route => ({ openPlanPicker: route.query.openPlanPicker }),
    },
    {
      path: Routes.analytics,
      view: 'Analytics',
    },
    {
      path: Routes.teamMembers,
      view: 'TeamMembers',
    },
    {
      path: Routes.linkShops,
      view: 'LinkShops',
    },
    {
      path: Routes.helpAndTutorials,
      view: 'HelpAndTutorials',
    },
    {
      path: Routes.planPick,
      view: 'PlanPick',
      hidden: true,
      parent: 'Account',
    },
  ]),
];

export const sections = paths.map(p => {
  const view = p.children ? p.children[0].name : p.name || p.view;

  return { view, name: p.meta.name || view, path: p.path };
});

export const shopifySections = sections.slice(1);

export default [
  {
    path: Routes.auth,
    view: 'Auth',
  },
  {
    path: Routes.welcome,
    view: 'Welcome',
    props: true,
  },

  ...paths,

  {
    path: Routes.customizeFacebook,
    view: 'CustomizeFacebook',
  },

  {
    path: Routes.integrationOld,
    view: 'IntegrationOld',
  },
  {
    path: Routes.customReviewForm,
    view: 'CustomReviewForm',
  },
  {
    path: Routes.group,
    view: 'GroupAdd',
  },
  {
    path: Routes.groupEdit,
    view: 'GroupEdit',
  },

  // {
  //   path: Routes.pickTrialPlan,
  //   view: 'PickTrialPlan',
  // },
  {
    path: Routes.trialEnded,
    view: 'TrialEnded',
  },

  {
    path: Routes.reviewImporterSettings,
    view: 'ReviewImporterSettings',
  },

  {
    path: Routes.getStarted,
    view: 'GetStarted',
  },

  {
    path: Routes.createCouponChooser,
    view: 'CreateCouponChooser',
    name: 'CreateCouponChooser',
  },
  {
    path: Routes.createCoupon,
    view: 'CreateCoupon',
    name: 'CreateCoupon',
    props: true,
  },

  {
    path: Routes.firstSteps,
    view: 'FirstSteps',
  },

  {
    path: Routes.installWizard,
    view: 'InstallWizard',
  },
  {
    path: Routes.requestWizard,
    view: 'RequestWizard',
  },

  {
    path: '*',
    redirect: { name: 'Overview' },
  },
];
